/*
| §84 publish modal. The toolbar button (data-publish-open) carries the current
| calendar view state in data-publish-state (JSON: {from, to, filters}) — kept in
| sync by calendar.ts the same way the export button is. Opening the modal loads
| a server preview (per-employee confirmed shift counts + warnings) and a click
| on "Publikovat" fires the publication.
*/
import { toast } from './toast';

interface PublishState { from?: string; to?: string; filters?: Record<string, string | number> }

const axios = (): any => (window as any).axios;

function esc(value: string): string {
    const div = document.createElement('div');
    div.textContent = value ?? '';
    return div.innerHTML;
}

function trans(template: string, replacements: Record<string, string | number>): string {
    return Object.entries(replacements).reduce(
        (text, [key, value]) => text.replace(`:${key}`, String(value)),
        template,
    );
}

let changedCount = 0;

export function initPublishModal(): void {
    const buttons = document.querySelectorAll<HTMLElement>('[data-publish-open]');
    buttons.forEach((btn) => {
        btn.addEventListener('click', () => open(btn));
    });
    if (buttons.length) void updateBadge();
}

async function updateBadge(): Promise<void> {
    const island = document.querySelector<HTMLElement>('script[data-planning-calendar]');
    if (!island) return;
    let pub: any = null;
    try { pub = JSON.parse(island.textContent ?? '{}').publications ?? null; } catch { return; }
    if (!pub?.routes?.status) return;
    try {
        const { data } = await (window as any).axios.get(pub.routes.status);
        changedCount = Number(data.changed_count ?? 0);
        const periods: any[] = data.unpublished_periods ?? [];
        document.querySelectorAll<HTMLElement>('[data-publish-badge]').forEach((badge) => {
            badge.textContent = changedCount > 0 ? String(changedCount) : '!';
            badge.hidden = changedCount === 0 && !periods.length;
        });
        // Deadline watcher: surface the first uncovered period in the button tooltip.
        if (periods.length && pub.i18n?.watcherUnpublished) {
            const message = trans(pub.i18n.watcherUnpublished, { date: periods[0].start, days: Number(periods[0].days_left) });
            document.querySelectorAll<HTMLElement>('[data-publish-open]').forEach((btn) => {
                btn.title = message;
            });
        }
    } catch { /* badge is decorative — a failed status fetch must not break the toolbar */ }
}

function open(btn: HTMLElement): void {
    const island = document.querySelector<HTMLElement>('script[data-planning-calendar]');
    if (!island) return;
    let cfg: any = {};
    try { cfg = JSON.parse(island.textContent ?? '{}'); } catch { return; }
    const pub = cfg.publications;
    if (!pub) return;

    let state: PublishState = {};
    try { state = JSON.parse(btn.dataset.publishState ?? '{}'); } catch { state = {}; }
    const filters = state.filters ?? {};
    const i18n = pub.i18n;

    const backdrop = document.createElement('div');
    backdrop.className = 'pl-modal-backdrop';
    const panel = document.createElement('div');
    panel.className = 'pl-modal wp-modal';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    const tabsHtml = pub.routes.list
        ? '<div class="pl-views mb-3">'
            + `<button type="button" class="pl-view" data-pub-tab="publish" aria-pressed="true">${esc(i18n.tabPublish)}</button>`
            + `<button type="button" class="pl-view" data-pub-tab="history" aria-pressed="false">${esc(i18n.tabHistory)}</button>`
            + '</div>'
        : '';
    const deltaHtml = (pub.routes.republish && changedCount > 0)
        ? '<label class="flex items-center gap-2 mb-3 text-sm"><input type="checkbox" data-pub-delta checked>'
            + `${esc(trans(i18n.deltaToggle, { count: changedCount }))}</label>`
        : '';
    panel.innerHTML =
        `<h2 class="wp-modal-title">${esc(i18n.title)}</h2>`
        + '<div class="wp-modal-body">'
        + tabsHtml
        + '<div data-pub-pane-publish>'
        + `<div class="flex items-center gap-2 mb-3"><span class="pl-muted text-sm">${esc(i18n.period)}</span>`
        + `<input type="date" class="wp-input" data-pub-from value="${esc(state.from ?? '')}">`
        + '<span>–</span>'
        + `<input type="date" class="wp-input" data-pub-to value="${esc(state.to ?? '')}"></div>`
        + deltaHtml
        + '<div data-pub-warnings class="space-y-1 mb-3"></div>'
        + `<div data-pub-preview class="pub-preview"><div class="pl-muted text-sm">${esc(i18n.loading)}</div></div>`
        + '</div>'
        + '<div data-pub-pane-history hidden></div>'
        + '</div>';

    const actions = document.createElement('div');
    actions.className = 'wp-modal-actions';
    const confirm = document.createElement('button');
    confirm.type = 'button';
    confirm.className = 'btn-cta';
    confirm.textContent = i18n.confirm;
    confirm.disabled = true;
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'pl-btn pl-btn-ghost';
    cancel.textContent = i18n.cancel;
    actions.append(confirm, cancel);
    panel.appendChild(actions);
    backdrop.appendChild(panel);

    const close = (): void => backdrop.remove();
    cancel.addEventListener('click', close);
    backdrop.addEventListener('click', (e) => { if (e.target === backdrop) close(); });
    document.addEventListener('keydown', function onKey(e) {
        if (e.key === 'Escape') { close(); document.removeEventListener('keydown', onKey); }
    });
    document.body.appendChild(backdrop);

    const fromInput = panel.querySelector<HTMLInputElement>('[data-pub-from]')!;
    const toInput = panel.querySelector<HTMLInputElement>('[data-pub-to]')!;
    const warningsBox = panel.querySelector<HTMLElement>('[data-pub-warnings]')!;
    const previewBox = panel.querySelector<HTMLElement>('[data-pub-preview]')!;

    const payload = (): Record<string, unknown> => ({
        period_start: fromInput.value,
        period_end: toInput.value,
        filters,
    });

    const renderPreview = (data: any): void => {
        const warnings: string[] = [];
        if (data.kind === 'partial') warnings.push(i18n.warnPartial);
        if (data.draft_count > 0) warnings.push(trans(i18n.warnDrafts, { count: data.draft_count }));
        if (data.open_count > 0) warnings.push(trans(i18n.warnOpen, { count: data.open_count }));
        if (data.closed_months.length > 0) warnings.push(trans(i18n.warnClosed, { months: data.closed_months.join(', ') }));
        warningsBox.innerHTML = warnings
            .map((w) => `<div class="pl-badge" style="background: var(--state-warn-soft, #fef3c7); color: var(--state-warn, #92400e)">${esc(w)}</div>`)
            .join('');

        if (!data.employees.length) {
            previewBox.innerHTML = `<div class="pl-muted text-sm">${esc(i18n.empty)}</div>`;
            confirm.disabled = true;
            return;
        }
        const rows = data.employees
            .map((e: any) => `<tr><td>${esc(e.name)}</td><td class="text-right">${Number(e.shift_count)}</td></tr>`)
            .join('');
        previewBox.innerHTML =
            '<table class="w-full text-sm">'
            + `<thead><tr><th class="text-left">${esc(i18n.employee)}</th><th class="text-right">${esc(i18n.shiftCount)}</th></tr></thead>`
            + `<tbody>${rows}</tbody></table>`
            + `<div class="pl-muted text-xs mt-2">${esc(trans(i18n.versionInfo, { version: data.next_version }))}</div>`;
        confirm.disabled = false;
    };

    // Delta mode — re-deliver only to affected employees (spec: "N změn nepublikováno").
    const deltaToggle = panel.querySelector<HTMLInputElement>('[data-pub-delta]');
    const isDelta = (): boolean => deltaToggle !== null && deltaToggle.checked;

    const changeTypeLabel = (type: string): string => {
        if (type === 'removed') return i18n.changeRemoved;
        if (type === 'added') return i18n.changeAdded;
        return i18n.changeModified;
    };

    const renderDelta = (data: any): void => {
        const underLead = data.affected.some((a: any) => a.changes.some((c: any) => c.under_lead));
        warningsBox.innerHTML = underLead
            ? `<div class="pl-badge" style="background: var(--state-warn-soft, #fef3c7); color: var(--state-warn, #92400e)">${esc(trans(i18n.underLead, { lead: Number(data.lead_days) }))}</div>`
            : '';

        if (!data.affected.length) {
            previewBox.innerHTML = `<div class="pl-muted text-sm">${esc(i18n.deltaEmpty)}</div>`;
            confirm.disabled = true;
            return;
        }
        previewBox.innerHTML = data.affected.map((a: any) => {
            const changes = a.changes.map((c: any) =>
                `<div class="text-xs${c.under_lead ? '' : ' pl-muted'}">`
                + `${esc(c.date)} · ${esc(changeTypeLabel(c.type))}: ${esc(c.before ?? '—')} → ${esc(c.after ?? '—')}`
                + '</div>').join('');
            return `<div class="py-1 border-t" style="border-color: var(--border)">`
                + `<div class="text-sm font-medium">${esc(a.name)} (${Number(a.shift_count)})</div>${changes}</div>`;
        }).join('')
            + `<div class="pl-muted text-xs mt-2">${esc(trans(i18n.versionInfo, { version: Number(data.next_version) }))}</div>`;
        confirm.disabled = false;
    };

    const loadPreview = async (): Promise<void> => {
        if (!fromInput.value || !toInput.value) return;
        confirm.disabled = true;
        previewBox.innerHTML = `<div class="pl-muted text-sm">${esc(i18n.loading)}</div>`;
        try {
            if (isDelta()) {
                const { data } = await axios().post(pub.routes.deltaPreview, payload());
                renderDelta(data);
            } else {
                const { data } = await axios().post(pub.routes.preview, payload());
                renderPreview(data);
            }
        } catch (e: any) {
            previewBox.innerHTML = `<div class="pl-muted text-sm">${esc(e?.response?.data?.message ?? 'Error')}</div>`;
        }
    };

    fromInput.addEventListener('change', () => void loadPreview());
    toInput.addEventListener('change', () => void loadPreview());
    deltaToggle?.addEventListener('change', () => void loadPreview());

    // History tab — publication list with per-employee delivery evidence.
    const publishPane = panel.querySelector<HTMLElement>('[data-pub-pane-publish]')!;
    const historyPane = panel.querySelector<HTMLElement>('[data-pub-pane-history]')!;

    const evidenceIcons = (item: any): string => {
        const parts: string[] = [];
        parts.push(item.sent_at
            ? `<span class="pl-mark" title="${esc(i18n.sent)}">✉</span>`
            : `<span class="pl-mark pl-mark-off" title="${esc(i18n.notSent)}">✉</span>`);
        if (item.seen_at) parts.push(`<span class="pl-mark" title="${esc(i18n.seen)}">👁</span>`);
        if (item.acknowledged_at) parts.push(`<span class="pl-mark" title="${esc(i18n.acknowledged)}">✔</span>`);
        return parts.join(' ');
    };

    const renderHistory = (publications: any[]): void => {
        if (!publications.length) {
            historyPane.innerHTML = `<div class="pl-muted text-sm">${esc(i18n.historyEmpty)}</div>`;
            return;
        }
        historyPane.innerHTML = publications.map((p: any) => {
            const kindBadge = p.kind !== 'full'
                ? ` <span class="pl-badge">${esc(p.kind === 'delta' ? i18n.kindDelta : i18n.kindPartial)}</span>`
                : '';
            const items = p.items.map((it: any) => {
                const error = it.mail_error
                    ? `<div class="text-xs" style="color: var(--state-danger, #b91c1c)">${esc(it.mail_error)}</div>`
                    : '';
                const resend = (it.mail_error || !it.sent_at)
                    ? `<button type="button" class="pl-btn pl-btn-ghost" data-pub-resend="${esc(it.resend_url)}">${esc(i18n.resend)}</button>`
                    : '';
                return '<div class="flex items-center gap-2 py-1 border-t" style="border-color: var(--border)">'
                    + `<span class="flex-1 text-sm">${esc(it.user)} (${Number(it.shift_count)})</span>`
                    + `<span class="text-sm">${evidenceIcons(it)}</span>`
                    + resend
                    + `<a class="pl-btn pl-btn-ghost" href="${esc(it.pdf_url)}" target="_blank" rel="noopener">${esc(i18n.download)}</a>`
                    + error
                    + '</div>';
            }).join('');
            const version = trans(i18n.historyVersion, { version: Number(p.version) });
            const published = new Date(p.published_at).toLocaleString('cs-CZ');
            return '<div class="mb-3">'
                + `<div class="font-medium text-sm">${esc(p.period_start)} – ${esc(p.period_end)} · ${esc(version)}${kindBadge}`
                + `<span class="pl-muted"> · ${esc(published)}${p.published_by ? ` · ${esc(p.published_by)}` : ''}</span></div>`
                + items
                + '</div>';
        }).join('');

        historyPane.querySelectorAll<HTMLElement>('[data-pub-resend]').forEach((btn) => {
            btn.addEventListener('click', async () => {
                btn.setAttribute('disabled', 'true');
                try {
                    const { data } = await axios().post(btn.dataset.pubResend);
                    toast(data.message);
                    void loadHistory();
                } catch (e: any) {
                    btn.removeAttribute('disabled');
                    toast(e?.response?.data?.message ?? 'Error');
                }
            });
        });
    };

    const loadHistory = async (): Promise<void> => {
        historyPane.innerHTML = `<div class="pl-muted text-sm">${esc(i18n.loading)}</div>`;
        try {
            const { data } = await axios().get(pub.routes.list);
            renderHistory(data.publications);
        } catch (e: any) {
            historyPane.innerHTML = `<div class="pl-muted text-sm">${esc(e?.response?.data?.message ?? 'Error')}</div>`;
        }
    };

    panel.querySelectorAll<HTMLElement>('[data-pub-tab]').forEach((tab) => {
        tab.addEventListener('click', () => {
            const isHistory = tab.dataset.pubTab === 'history';
            panel.querySelectorAll<HTMLElement>('[data-pub-tab]').forEach((t) => {
                t.setAttribute('aria-pressed', String(t === tab));
            });
            publishPane.hidden = isHistory;
            historyPane.hidden = !isHistory;
            confirm.hidden = isHistory;
            if (isHistory) void loadHistory();
        });
    });

    confirm.addEventListener('click', async () => {
        confirm.disabled = true;
        try {
            const url = isDelta() ? pub.routes.republish : pub.routes.store;
            const { data } = await axios().post(url, payload());
            toast(data.message);
            close();
            document.dispatchEvent(new CustomEvent('planning:refresh-shifts'));
            void updateBadge();
        } catch (e: any) {
            confirm.disabled = false;
            toast(e?.response?.data?.message ?? 'Error');
        }
    });

    void loadPreview();
}
